import { inject, Injectable } from "@angular/core";
import { SearchResultItem, SearchState } from "../../model";

// Core map imports
import { MapService } from "../../../viewer-components/services/internal-services/map.service";
import {
  Circle,
  Feature as CustomFeature,
  Fill,
  GeometryCollection,
  Location,
  OverlayManager,
  Point as CustomPoint,
  Stroke,
  Style,
  VectorLayer,
  WKT,
} from "@vision/core-map-api";

import { FeatureEvents } from "@vision/core-map-api/src/constants/CoreMapConstants";
import { GeoService } from "../../../viewer-components/services/internal-services/geo.service";
import { Heatmap } from "ol/layer";
import VectorSource from "ol/source/Vector";
import { Point } from "ol/geom";
import { Feature } from "ol";
import { Subject } from "rxjs";

/**
 * Interface for tracking search layer state
 */
interface SearchLayerState {
  layerId: string;
  layerExists: boolean;
  featuresCount: number;
  currentSearchId: string | null;
  lastBounds: number[]; // For potential future auto-fit implementation
}

/**
 * Interface for heatmap configuration
 */
interface HeatmapConfig {
  enabled: boolean;
  radius: number;
  blur: number;
  minResolution: number;
}

/**
 * Service for managing search results layers on the map
 * Handles layer creation, feature management, and styling
 */
@Injectable()
export class MapSearchLayerService {
  mapService = inject(MapService);

  private readonly SEARCH_LAYER_ID = "search-results-layer";

  private readonly HEATMAP_LAYER_ID = "search-heatmap-layer";

  private layerState: SearchLayerState = {
    layerId: this.SEARCH_LAYER_ID,
    layerExists: false,
    featuresCount: 0,
    currentSearchId: null,
    lastBounds: null,
  };

  private heatmapConfig: HeatmapConfig = {
    enabled: true,
    radius: 5,
    blur: 15,
    minResolution: 70,
  };

  /**
   * Emits the feature that was clicked on the map search layer.
   */
  private featureClickSubject = new Subject<any>();

  /**
   * Observable for feature click events.
   */
  public featureClick$ = this.featureClickSubject.asObservable();

  getSearchExtent(): number[] | null {
    return this.layerState.lastBounds;
  }

  /**
   * Computes the bounding box (WGS84) of the given results' centroids, without
   * touching the map layer. Used to zoom to the results area before the layer itself is drawn.
   * @param results Search result items
   * @returns [minX, minY, maxX, maxY] or null if no valid centroids are present
   */
  computeResultsExtent(results: SearchResultItem[]): number[] | null {
    const centroidPoints = results
      .filter((r) => Array.isArray(r.centroid) && r.centroid.length === 2)
      .map((r) => r.centroid);
    return centroidPoints.length > 0 ? GeoService.bboxFromCoordinates(centroidPoints) : null;
  }

  /**
     * Main method to process search results and update map layer
     * @param searchState Current search state with results

     */
  processSearchResults(searchState: SearchState): void {
    if (searchState.loading || !searchState.results.length) {
      return;
    }
    // If new search, clear previous results
    if (searchState.isNewSearch) {
      this.clearSearchLayer();
      this.layerState.currentSearchId = searchState.searchId;
    }

    // Create or get existing layer
    const layer = this.getSearchLayer();

    // Add new features to the layer
    this.addFeaturesToLayer(layer, searchState.results);

    this.updateHeatmapData(layer.getFeatures().map((feature) => feature.clone()));

    // Calculate extent (bounding box) of all centroids using Turf.js
    try {
      const extent = this.computeResultsExtent(searchState.results);
      if (extent) {
        this.layerState.lastBounds = extent; // [minX, minY, maxX, maxY]
      }
    } catch (e) {
      console.error("Error calculating extent for centroids:", e);
    }

    // Update layer state
    this.layerState.featuresCount += searchState.results.length;
  }

  /**
     * Clear the search results layer

     */
  clearSearchLayer(): void {
    const layer = this.getSearchLayer();
    if (layer) {
      try {
        layer.clear();
        this.layerState.layerExists = false;
        this.layerState.featuresCount = 0;
        this.layerState.currentSearchId = null;
      } catch (e) {
        // Layer doesn't exist or already removed
        console.warn(`Could not remove search layer: ${e.message}`);
      }
    }
    const heatmap = this.getHeatmapLayer();
    if (heatmap && heatmap.getSource()) {
      try {
        heatmap.getSource().clear();
      } catch (e) {
        // Layer doesn't exist or already removed
        console.warn(`Could not remove search layer: ${e.message}`);
      }
    }
  }

  /**
     * Ensure the search layer exists, create if it doesn't

     */
  private getSearchLayer(): VectorLayer {
    const existingLayer = this.getOverlayManager().getLayerById(this.layerState.layerId);
    if (!existingLayer) {
      return this.createSearchLayer();
    }
    return existingLayer;
  }

  private getHeatmapLayer(): Heatmap {
    const existingLayer = this.mapService.findLayer(this.HEATMAP_LAYER_ID);
    if (!existingLayer) {
      return this.createHeatmapLayer();
    }
    return existingLayer;
  }

  private updateHeatmapData(features: CustomFeature[]) {
    if (!this.heatmapConfig.enabled) {
      return;
    }

    const layerHeatmap = this.getHeatmapLayer();
    const olFeatures = features.map((f) => {
      const point = f
        .get("geometry")
        .getGeometries()
        .find((g) => g.getType() === "Point");
      const feat = new Feature(new Point(point.getCoordinates() as [number, number]));
      feat.set("similarity", f.get("similarity"));
      return feat;
    });
    layerHeatmap.getSource().clear();
    layerHeatmap.getSource().addFeatures(olFeatures);
  }

  /**
   * Create a new empty search results layer with the style function
   */
  private createSearchLayer(): VectorLayer {
    // Assign the style function directly to the layer
    const emptyLayer = new VectorLayer({
      title: "Search results",
      id: this.layerState.layerId,
      features: [],
      style: this.layerStyleFunction.bind(this),
    });
    this.getOverlayManager().addLayer(emptyLayer);
    this.layerState.layerExists = true;
    return emptyLayer;
  }

  /**
   * Create a new empty search results layer with the style function
   */
  private createHeatmapLayer(): Heatmap {
    // Assign the style function directly to the layer
    const emptyLayer = new Heatmap({
      minResolution: this.heatmapConfig.minResolution,
      source: new VectorSource({}),
      blur: this.heatmapConfig.blur,
      radius: this.heatmapConfig.radius,
      weight: (feature) => feature.get("score") ?? 0.5,
      gradient: [
        "#9e1a1a", // low (soft red)
        "#da5128", // mid-light neutral
        "#f8c152", // mid green-olive
        "#02bd20",
      ], // high (soft green)]
    });
    emptyLayer.set("id", this.HEATMAP_LAYER_ID);
    emptyLayer.set("title", "Search heatmap");
    emptyLayer.setVisible(this.heatmapConfig.enabled);
    this.mapService.addLayer(emptyLayer);
    return emptyLayer;
  }

  /**
   * Add search result features to the existing layer
   * @param layer
   * @param results Search result items to add
   */
  private addFeaturesToLayer(layer: VectorLayer, results: SearchResultItem[]): void {
    // Create new features, only those that do not already exist
    const features = this.createFeaturesFromResults(results);
    // Add only new features
    if (features.length > 0) {
      layer.clear();
      features.forEach((feature) => {
        feature.addEventListener(FeatureEvents.FEATURE_CLICK, this.handleFeatureClick.bind(this));
      });
      layer.addFeatures(features);
    }
  }

  /**
   * Handles click events on map features.
   * @param event The feature click event.
   */
  private handleFeatureClick(event: any): void {
    this.featureClickSubject.next(event.feature);
  }

  private layerStyleFunction(feature: CustomFeature, resolution: number) {
    const geometry = feature.getGeometry();
    const styles: Style[] = [];
    const color = feature.get("color");
    const highResolution = resolution > 70; // zoom 18 aprox
    if (geometry.getType() === "GeometryCollection") {
      geometry.getGeometries().forEach((geom) => {
        if (!highResolution) {
          if (geom.getType() === "Polygon" || geom.getType() === "MultiPolygon") {
            styles.push(
              new Style({
                stroke: new Stroke({
                  color: color,
                  width: 2,
                }),
                fill: new Fill({
                  color: "rgba(0,0,0,0)",
                }),
                geometry: geom,
                zIndex: feature.get("score"),
              }),
            );
          }
        }
        if (geom.getType() === "Point") {
          styles.push(
            new Style({
              image: new Circle({
                radius: 4,
                fill: new Fill({
                  color: color,
                  opacity: highResolution ? 1 : 0.4,
                }),
              }),
              geometry: geom,
              zIndex: feature.get("score"),
            }),
          );
        }
      });
    }

    return styles;
  }

  /**
   * Create styled features from search results
   * @param results Search result items
   * @returns Array of styled features
   */
  private createFeaturesFromResults(results: SearchResultItem[]): any[] {
    const features = [];
    const wktReader = new WKT();

    results.forEach((result) => {
      try {
        // Create polygon feature from WKT geometry
        const multiPolygon = wktReader.readGeometry(result.geometry);
        const centroidPoint = new CustomPoint(new Location(result.centroid[1], result.centroid[0]));
        const feature = new CustomFeature(new GeometryCollection([multiPolygon, centroidPoint]));
        feature.set("id", `${result.centroid[1]},${result.centroid[0]}`);
        feature.set("similarity", result.similarity);
        feature.set("score", result.score);
        feature.set("color", result.scoreColor);
        features.push(feature);
      } catch (error) {
        console.error(`Error creating feature for result ${result.key}:`, error);
      }
    });

    return features.slice(0).reverse();
  }

  public getOverlayManager(): OverlayManager {
    return this.mapService.getOverlayManager();
  }

  /**
   * Get the current heatmap configuration
   */
  public getHeatmapConfig(): HeatmapConfig {
    return { ...this.heatmapConfig };
  }

  /**
   * Update heatmap enabled state
   * @param enabled Whether the heatmap should be visible
   */
  public setHeatmapEnabled(enabled: boolean): void {
    this.heatmapConfig.enabled = enabled;
    const heatmapLayer = this.getHeatmapLayer();
    if (heatmapLayer) {
      heatmapLayer.setVisible(enabled);
    }
  }

  /**
   * Update heatmap radius in real-time
   * @param radius The radius value (typically 1-50)
   */
  public setHeatmapRadius(radius: number): void {
    this.heatmapConfig.radius = radius;
    const heatmapLayer = this.getHeatmapLayer();
    if (heatmapLayer) {
      heatmapLayer.setRadius(radius);
    }
  }

  /**
   * Update heatmap blur in real-time
   * @param blur The blur value (typically 1-50)
   */
  public setHeatmapBlur(blur: number): void {
    this.heatmapConfig.blur = blur;
    const heatmapLayer = this.getHeatmapLayer();
    if (heatmapLayer) {
      heatmapLayer.setBlur(blur);
    }
  }

  /**
   * Update multiple heatmap properties at once
   * @param config Partial configuration to update
   */
  public updateHeatmapConfig(config: Partial<HeatmapConfig>): void {
    const previousEnabled = this.heatmapConfig.enabled;
    Object.assign(this.heatmapConfig, config);

    const heatmapLayer = this.getHeatmapLayer();
    if (heatmapLayer) {
      if (config.enabled !== undefined) {
        heatmapLayer.setVisible(config.enabled);
      }
      if (config.radius !== undefined) {
        heatmapLayer.setRadius(config.radius);
      }
      if (config.blur !== undefined) {
        heatmapLayer.setBlur(config.blur);
      }
    }

    // If heatmap was disabled and now enabled, refresh data
    if (!previousEnabled && this.heatmapConfig.enabled && this.layerState.featuresCount > 0) {
      const searchLayer = this.getSearchLayer();
      if (searchLayer) {
        this.updateHeatmapData(searchLayer.getFeatures());
      }
    }
  }
}
